import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Plus,
  Pencil,
  Trash2,
  Sparkles,
  Eye,
  EyeOff,
  Save,
  X,
  Loader2,
  FileText,
  Calendar,
} from "lucide-react";

const CATEGORIES = [
  "Strategy",
  "Operations",
  "Growth",
  "Vendor coordination",
  "Cash-pay programs",
  "Product",
  "Founder notes",
];

const EMPTY_FORM = {
  id: null,
  slug: "",
  title: "",
  excerpt: "",
  content: "",
  author: "Nodera Editorial",
  category: "Strategy",
  read_minutes: 5,
  cover_image: "",
  published_at: "",
  published: true,
};

function slugify(s) {
  return (s || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/[\s_-]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

export default function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null); // null = list view; object = editor view
  const [saving, setSaving] = useState(false);

  // AI panel state
  const [aiOpen, setAiOpen] = useState(false);
  const [aiTopic, setAiTopic] = useState("");
  const [aiAngle, setAiAngle] = useState("");
  const [aiBusy, setAiBusy] = useState(false);

  const loadPosts = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/blog/posts");
      setPosts(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
  }, []);

  const startCreate = () => {
    setForm({ ...EMPTY_FORM });
    setAiOpen(false);
  };

  const startEdit = (post) => {
    setForm({
      id: post.id,
      slug: post.slug || "",
      title: post.title || "",
      excerpt: post.excerpt || "",
      content: post.content || "",
      author: post.author || "Nodera Editorial",
      category: post.category || "Strategy",
      read_minutes: post.read_minutes || 5,
      cover_image: post.cover_image || "",
      published_at: post.published_at || "",
      published: post.published !== false,
    });
    setAiOpen(false);
  };

  const cancelEdit = () => {
    setForm(null);
    setAiOpen(false);
    setAiTopic("");
    setAiAngle("");
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required.");
      return;
    }
    setSaving(true);
    try {
      const body = {
        slug: form.slug || slugify(form.title),
        title: form.title,
        excerpt: form.excerpt,
        content: form.content,
        author: form.author,
        category: form.category,
        read_minutes: Number(form.read_minutes) || 5,
        cover_image: form.cover_image,
        published_at: form.published_at || undefined,
        published: form.published,
      };
      if (form.id) {
        await api.patch(`/admin/blog/posts/${form.id}`, body);
        toast.success("Post saved.");
      } else {
        await api.post("/admin/blog/posts", body);
        toast.success("Post created.");
      }
      await loadPosts();
      setForm(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Post deleted.");
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.patch(`/admin/blog/posts/${post.id}`, {
        published: !(post.published !== false),
      });
      await loadPosts();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed.");
    }
  };

  const handleAIGenerate = async () => {
    if (!aiTopic.trim()) {
      toast.error("Enter a topic for the AI to write about.");
      return;
    }
    setAiBusy(true);
    try {
      const r = await api.post("/admin/blog/ai-draft", {
        topic: aiTopic.trim(),
        angle: aiAngle.trim() || null,
        category: form?.category || "Strategy",
        audience: "clinic operators",
      });
      const draft = r.data;
      setForm((f) => ({
        ...(f || EMPTY_FORM),
        title: draft.title || f?.title || "",
        excerpt: draft.excerpt || f?.excerpt || "",
        slug: draft.slug || slugify(draft.title || ""),
        content: draft.content || f?.content || "",
        read_minutes: draft.read_minutes || 5,
        category: draft.category || f?.category || "Strategy",
      }));
      setAiOpen(false);
      toast.success("Draft generated — review and publish.");
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "AI generation failed.";
      toast.error(typeof msg === "string" ? msg : "AI generation failed.");
    } finally {
      setAiBusy(false);
    }
  };

  // -------------- EDITOR --------------
  if (form) {
    return (
      <div data-testid="admin-blog-editor" className="max-w-5xl mx-auto py-8 px-4 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
              {form.id ? "Editing post" : "New post"}
            </div>
            <h1 className="mt-1 font-display text-2xl font-bold text-slate-900">
              {form.id ? form.title || "Untitled post" : "Compose a new essay"}
            </h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={cancelEdit}
              data-testid="admin-blog-cancel-btn"
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              <X className="w-4 h-4" /> Cancel
            </button>
            <button
              onClick={() => setAiOpen((o) => !o)}
              data-testid="admin-blog-ai-toggle"
              className="inline-flex items-center gap-1.5 rounded-lg border border-violet-300 bg-violet-50 px-3 py-2 text-sm font-semibold text-violet-700 hover:bg-violet-100"
            >
              <Sparkles className="w-4 h-4" /> Generate with Claude
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              data-testid="admin-blog-save-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Save
            </button>
          </div>
        </div>

        {aiOpen && (
          <div
            data-testid="admin-blog-ai-panel"
            className="rounded-2xl border border-violet-200 bg-violet-50/40 p-5 space-y-3"
          >
            <div className="flex items-center gap-2 text-sm font-semibold text-violet-800">
              <Sparkles className="w-4 h-4" /> Claude Sonnet 4.5 · Generate a draft
            </div>
            <p className="text-xs text-violet-700/80">
              Provide a topic + optional angle. The model returns a title, excerpt, slug, and
              500–800 word essay — drop-in editable below.
            </p>
            <input
              data-testid="admin-blog-ai-topic"
              type="text"
              value={aiTopic}
              onChange={(e) => setAiTopic(e.target.value)}
              placeholder="e.g. Why subscription billing is the real moat for clinics"
              className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm"
            />
            <input
              data-testid="admin-blog-ai-angle"
              type="text"
              value={aiAngle}
              onChange={(e) => setAiAngle(e.target.value)}
              placeholder="Optional angle (e.g. 'tactical playbook with margin math')"
              className="w-full rounded-lg border border-violet-200 bg-white px-3 py-2 text-sm"
            />
            <div className="flex items-center gap-2 pt-1">
              <button
                onClick={handleAIGenerate}
                disabled={aiBusy || !aiTopic.trim()}
                data-testid="admin-blog-ai-generate-btn"
                className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-50"
              >
                {aiBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                Generate draft
              </button>
              <button
                onClick={() => setAiOpen(false)}
                className="text-xs text-violet-700 hover:underline"
              >
                Close
              </button>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main editor */}
          <div className="lg:col-span-2 space-y-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Title
              </label>
              <input
                data-testid="admin-blog-title"
                type="text"
                value={form.title}
                onChange={(e) =>
                  setForm({
                    ...form,
                    title: e.target.value,
                    slug: form.id ? form.slug : slugify(e.target.value),
                  })
                }
                placeholder="Punchy headline"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2.5 text-lg font-semibold focus:border-slate-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Slug
              </label>
              <input
                data-testid="admin-blog-slug"
                type="text"
                value={form.slug}
                onChange={(e) => setForm({ ...form, slug: slugify(e.target.value) })}
                placeholder="auto-generated-from-title"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Excerpt
              </label>
              <textarea
                data-testid="admin-blog-excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                rows={2}
                placeholder="1–2 sentence dek"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                Content
              </label>
              <textarea
                data-testid="admin-blog-content"
                value={form.content}
                onChange={(e) => setForm({ ...form, content: e.target.value })}
                rows={20}
                placeholder="The essay. Paragraphs separated by blank lines."
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm leading-[1.7] font-serif"
              />
              <div className="mt-1 text-[11px] text-slate-500">
                {form.content.split(/\s+/).filter(Boolean).length} words · ~
                {Math.max(1, Math.round(form.content.split(/\s+/).filter(Boolean).length / 220))} min read
              </div>
            </div>
          </div>

          {/* Side panel: meta */}
          <aside className="space-y-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-4">
              <label className="flex items-center justify-between gap-3 cursor-pointer">
                <div>
                  <div className="text-sm font-semibold text-slate-900">Published</div>
                  <div className="text-xs text-slate-500">Hidden from /blog when off.</div>
                </div>
                <input
                  data-testid="admin-blog-published-toggle"
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="w-4 h-4"
                />
              </label>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Category
                </label>
                <select
                  data-testid="admin-blog-category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Author
                </label>
                <input
                  data-testid="admin-blog-author"
                  type="text"
                  value={form.author}
                  onChange={(e) => setForm({ ...form, author: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Publish date
                </label>
                <input
                  data-testid="admin-blog-publish-date"
                  type="date"
                  value={(form.published_at || "").slice(0, 10)}
                  onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">
                  Cover image URL
                </label>
                <input
                  data-testid="admin-blog-cover-image"
                  type="url"
                  value={form.cover_image}
                  onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                  placeholder="https://…"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-mono"
                />
                {form.cover_image && (
                  <div className="mt-2 aspect-video rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                    <img
                      src={form.cover_image}
                      alt="cover"
                      className="w-full h-full object-cover"
                      onError={(e) => (e.currentTarget.style.display = "none")}
                    />
                  </div>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    );
  }

  // -------------- LIST --------------
  return (
    <div data-testid="admin-blog-list" className="max-w-6xl mx-auto py-8 px-4 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-semibold text-slate-500">
            Content marketing
          </div>
          <h1 className="mt-1 font-display text-3xl font-bold text-slate-900">Blog</h1>
          <p className="mt-1 text-sm text-slate-600">
            Author essays for clinic operators. Drafts can be AI-generated via Claude and
            stay unpublished until you flip the toggle.
          </p>
        </div>
        <button
          onClick={startCreate}
          data-testid="admin-blog-new-btn"
          className="inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-800"
        >
          <Plus className="w-4 h-4" /> New post
        </button>
      </div>

      {loading ? (
        <div className="text-sm text-slate-500">Loading posts…</div>
      ) : posts.length === 0 ? (
        <div
          data-testid="admin-blog-empty"
          className="rounded-2xl border border-dashed border-slate-300 p-10 text-center"
        >
          <FileText className="w-8 h-8 mx-auto text-slate-400" />
          <div className="mt-3 font-semibold text-slate-700">No posts yet.</div>
          <div className="text-sm text-slate-500 mt-1">
            Click "New post" to compose, or generate a draft with Claude.
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Published</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`admin-blog-row-${p.slug}`}
                  className="border-t border-slate-100 hover:bg-slate-50/60"
                >
                  <td className="px-4 py-3">
                    <div className="font-semibold text-slate-900">{p.title}</div>
                    <div className="text-xs text-slate-500 font-mono truncate max-w-md">
                      /{p.slug}
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                    {p.category}
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <Calendar className="w-3 h-3" /> {(p.published_at || "").slice(0, 10)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => togglePublished(p)}
                      data-testid={`admin-blog-toggle-${p.slug}`}
                      className={
                        p.published !== false
                          ? "inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1 rounded-full"
                          : "inline-flex items-center gap-1 text-xs font-semibold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-1 rounded-full"
                      }
                    >
                      {p.published !== false ? (
                        <>
                          <Eye className="w-3 h-3" /> Live
                        </>
                      ) : (
                        <>
                          <EyeOff className="w-3 h-3" /> Draft
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-2">
                      <button
                        onClick={() => startEdit(p)}
                        data-testid={`admin-blog-edit-${p.slug}`}
                        className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(p)}
                        data-testid={`admin-blog-delete-${p.slug}`}
                        className="inline-flex items-center gap-1 rounded-md border border-rose-200 bg-white px-2.5 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
